'use client';

import clsx from 'clsx';
import { FC, ReactElement } from 'react';
import useSWRImmutable from 'swr/immutable';
import { shallow } from 'zustand/shallow';

import { SEARCH_POPOVER_BUTTON_ID } from 'src/constants/constants';
import { IconSearch } from 'src/general/Icons/IconSearch';
import { Button } from 'src/general/components/Button/Button';
import { Divider } from 'src/general/components/Divider/Divider';
import { Link } from 'src/general/components/Link/Link';
import { getPLPCarData } from 'src/services/CarService';
import { modalStore } from 'src/stores/modalStore';
import { ProductListDataPayload } from 'src/types/CataloguePage.types';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

type Props = {
  totalCars: ReactElement;
};

export const FindACar: FC<Props> = ({ totalCars }) => {
  const { openModal } = modalStore((state) => ({ openModal: state.openModal }), shallow);
  const { data: productList } = useSWRImmutable<ProductListDataPayload>(
    {
      pageSize: 1, // Minimum valid pageSize
    },
    getPLPCarData,
  );

  const total = productList?.total ?? 0;

  return (
    <aside className="rounded-lg bg-ind-700 p-4 text-tex-inv-tie1" data-testid="homepage-search-input">
      <SearchButton
        className="md:hidden"
        onClick={() => {
          openModal('search');
          pushToDataLayer({
            event: 'search_click_search',
          });
        }}
      />
      <SearchButton
        id={SEARCH_POPOVER_BUTTON_ID}
        className="hidden md:block"
        onClick={() => {
          openModal('searchPopover');
          pushToDataLayer({
            event: 'search_click_search',
          });
        }}
      />
      <Divider>or</Divider>
      <Button
        data-testid="homepage-search-all-cars"
        href="/used-cars"
        fullWidth
        variant="accent"
        onClick={() => {
          pushToDataLayer({
            event: 'search_click_browse_all_cars',
            value: total,
          });
        }}
      >
        Browse all {totalCars} cars
      </Button>
      <footer className="mt-2 text-center">
        <span className="c-fs-body2 font-normal">
          Looking to sell your car?{' '}
          <Link href="/sell-or-trade-in-my-car" className="font-extrabold underline hover:no-underline">
            Get a valuation
          </Link>
        </span>
      </footer>
    </aside>
  );
};

const SearchButton: FC<{ id?: string; className?: string; onClick?: () => void }> = ({ id, className, onClick }) => {
  return (
    <button
      id={id}
      className={clsx(
        className,
        'c-fs-body1 relative h-14 w-full cursor-pointer rounded-sm bg-bac-tie1 pr-14 pl-4 text-left font-normal text-tex-def-tie2',
      )}
      onClick={onClick}
      data-testid="homepage-search-button"
    >
      <span>Search makes, models, body...</span>
      <span className="absolute top-1/2 right-4 -translate-y-1/2 text-tex-def-tie1">
        <IconSearch />
      </span>
    </button>
  );
};
